// Product chapters. Each story is a paused timeline that AUTOPLAYS while its
// chapter is on screen and pauses when it leaves; the scroll is never held (no pinning).
import { $, $$, G, ST, fmt, rand } from './misc'

/** The story timeline of a chapter, so the portal can rewind it on a menu jump. */
export const chapterTimelines = new WeakMap<Element, gsap.core.Timeline>()

// Chapters whose story the visitor took over (a cobranzas tab): they hold instead of looping.
// The value cancels whatever the visitor started (the tween to the picked tab), so leaving the
// chapter or replaying it never has two things driving one timeline.
const heldByVisitor = new WeakMap<Element, () => void>()
export const holdChapter = (sec: Element, cancel: () => void): void => { heldByVisitor.set(sec, cancel) }
const release = (sec: Element): void => { heldByVisitor.get(sec)?.(); heldByVisitor.delete(sec) }

// Seconds a finished story stays on its last frame before it plays again.
const LOOP_HOLD = 4.5

/**
 * Plays a chapter's story while it is on screen. Arriving again tells it from the start, and a
 * story that ends while still on screen holds its last frame, then plays again: the visitor never
 * has to reload to see a module work.
 */
const autoplay = (tl: gsap.core.Timeline, sec: Element, rate = 1.15): void => {
  tl.timeScale(rate)
  chapterTimelines.set(sec, tl)
  let active = false
  let again: gsap.core.Tween | null = null
  // Rewind with events on, so text typed by onUpdate callbacks is wiped too.
  const restart = () => { again?.kill(); tl.pause(); tl.seek(0, false); tl.play() }
  tl.eventCallback('onComplete', () => {
    again?.kill()
    again = active && !heldByVisitor.has(sec) ? G.delayedCall(LOOP_HOLD, () => { if (active && !heldByVisitor.has(sec)) restart() }) : null
  })
  ST.create({
    trigger: sec, start: 'top 62%', end: 'bottom 30%',
    onToggle: (st) => {
      active = st.isActive
      if (!active) { again?.kill(); release(sec); tl.pause(); return }
      release(sec)
      // First arrival plays; any later arrival tells the story again from its first step.
      if (tl.progress() > 0) restart()
      else tl.play()
    },
  })
}

// Example conversations for the copilot (sample data, shown as such on the page).
interface QaCard { t: string; s: string; p: string; tone?: string }
interface Qa { q: string; a: string; cards: QaCard[]; act: string[] }
const QA: Qa[] = [
  { q: '¿Qué pólizas vencen esta semana?', a: 'Encontré 23 pólizas que vencen esta semana. Estas son las más urgentes:',
    cards: [{ t: 'Juan Pérez', s: 'Automotores, Galeno', p: 'lun 3/11', tone: 'r' }, { t: 'Lucía Gómez', s: 'Hogar, Sancor', p: 'mié 5/11', tone: 'a' }, { t: 'Carlos Díaz', s: 'Vida, Zurich', p: 'vie 7/11', tone: 'a' }],
    act: ['Ver las 23 pólizas', 'Abrir la agenda'] },
  { q: '¿Quiénes tienen cuotas vencidas?', a: 'Hay 14 clientes con cuotas vencidas, por $ 612.400 en total. Los tres con más deuda:',
    cards: [{ t: 'Pablo Sosa', s: '3 cuotas, Automotores', p: '$ 134.700', tone: 'r' }, { t: 'Ana Torres', s: '2 cuotas, Hogar', p: '$ 61.300', tone: 'r' }, { t: 'Sofía Ríos', s: '1 cuota, Vida', p: '$ 18.900', tone: 'a' }],
    act: ['Ver la cuenta corriente', 'Ver los 14 clientes'] },
  { q: '¿Por qué falló la importación de ayer?', a: 'La importación de ayer trajo 3 pólizas con un código de productor que no tenés cargado. Las otras 1.284 entraron bien.',
    cards: [{ t: '1.284 pólizas', s: 'importadas sin problemas', p: 'OK', tone: 'g' }, { t: '3 pólizas', s: 'código de productor sin asignar', p: 'Revisar', tone: 'a' }],
    act: ['Asignar el código', 'Ver la importación'] },
  { q: 'Cargá los clientes de este Excel', a: 'Reconocí las columnas y encontré 214 clientes. 12 ya existían y los uní con su ficha, y 3 tienen el documento incompleto.',
    cards: [{ t: '199 nuevos', s: 'listos para cargar', p: 'OK', tone: 'g' }, { t: '12 duplicados', s: 'unidos al cliente existente', p: 'OK', tone: 'g' }, { t: '3 rechazos', s: 'documento incompleto', p: 'Revisar', tone: 'a' }],
    act: ['Confirmar la carga'] },
  { q: '¿Cómo emito una póliza de auto?', a: 'Entrás a Cotizar, buscás por patente y elegís la cobertura. Después tocás Emitir: si la compañía pide inspección, cargás las fotos ahí mismo y la póliza queda en la ficha del cliente.',
    cards: [], act: ['Ir a Cotizar'] },
]
const cardsHTML = (x: Qa) => (x.cards.length ? `<div class="cb-cards">${x.cards.map((c) => `<div class="cc"><b>${c.t}</b><small>${c.s}</small><em class="${c.tone || ''}">${c.p}</em></div>`).join('')}</div>` : '')
const actsHTML = (x: Qa) => `<div class="cb-acts">${x.act.map((s) => `<span>${s}</span>`).join('')}</div>`

/**
 * The chapters, in page order. The entrypoint wires each one as its own task (yielding in
 * between), and each stands alone: one that fails to wire must not take the others down.
 */
export const CHAPTERS: readonly (readonly [string, () => void])[] = [
  ['tilts', wireTilts], ['ia', wireIa], ['copilot', wireCopilot], ['quoter', wireQuoter],
  ['plata', wirePlata], ['stack', wireStack], ['mail', wireMail], ['whatsapp', wireWhatsapp],
]

/** Product windows tilt into place in 3D as their chapter arrives. */
function wireTilts(): void {
  if ($('#ia .win')) {
    G.fromTo('#ia .win', { rotateX: 24, rotateY: -10, scale: 0.86, y: 90, opacity: 0.35 },
      { rotateX: 0, rotateY: 0, scale: 1, y: 0, opacity: 1, ease: 'none', scrollTrigger: { trigger: '#ia', start: 'top bottom', end: 'top top', scrub: true } })
  }
  if ($('.coti-ui') && $('#cotizador')) {
    G.fromTo('.coti-ui', { rotateX: 20, y: 90, scale: 0.92, transformPerspective: 1400 },
      { rotateX: 0, y: 0, scale: 1, ease: 'none', scrollTrigger: { trigger: '#cotizador', start: 'top bottom', end: 'top top', scrub: true } })
  }
}

/** Asesor IA: the PDF drops, the AI reads it field by field, then answers about the portfolio. */
function wireIa(): void {
  const sec = $('#ia')
  if (!sec) return
  const drop = $('.drop', sec)!; const file = $('.file', sec)!; const doc = $('.doc', sec)!; const scan = $('.scan', sec)!
  const fields = $$('.f', sec); const lines = $$('.dl', sec); const steps = $$('.step', sec)
  const bar = $('.bar i', sec)!; const cuotas = $$('.cuotas i', sec); const wfS = $('.wf-s', sec)!
  const aip = $('.aip', sec)!; const ai = $('.msg.ai', sec)!; const q = $('.q', sec)!; const rcs = $$('.rc', sec)
  const texts = fields.map((f) => { const v = $('.v', f)!; return { v, t: v.dataset.t || '', plate: v.hasAttribute('data-plate') } })
  texts.forEach((x) => { x.v.textContent = '' })
  G.set(fields.map((f) => $('.c', f)), { opacity: 0, scale: 0.5 })
  G.set(fields, { boxShadow: 'inset 0 0 0 1px rgba(226,232,240,1), 0 0 0 0px rgba(56,189,248,0)' })
  G.set(lines, { backgroundColor: 'rgba(56,189,248,0)' })
  G.set(steps, { opacity: 0.32 })
  G.set(steps[0], { opacity: 1 })
  G.set(file, { xPercent: -50, yPercent: -50, y: 240, rotate: -10, opacity: 0 })
  G.set(doc, { opacity: 0, scale: 0.9 })
  G.set(bar, { scaleX: 0 })
  G.set(cuotas, { backgroundColor: '#CBD5E1' })
  G.set(aip, { xPercent: 104 })
  G.set([ai, ...rcs], { opacity: 0, y: 14 })
  const qt = q.dataset.t || ''; q.textContent = ''
  wfS.textContent = 'Esperando el PDF'
  const tl = G.timeline({ defaults: { ease: 'none' }, paused: true }); autoplay(tl, sec)
  tl.to($('i', steps[0]), { scaleY: 1, duration: 1.4 }, 0)
    .to(file, { y: 0, rotate: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0)
    .to(drop, { borderColor: '#38BDF8', backgroundColor: 'rgba(234,247,255,.98)', duration: 0.3 }, 0.7)
    .to(file, { scale: 0.3, opacity: 0, duration: 0.35, ease: 'power2.in' }, 1.05)
    .to(drop, { opacity: 0, duration: 0.3 }, 1.15)
    .to(doc, { opacity: 1, scale: 1, duration: 0.4, ease: 'power3.out' }, 1.2)
    .to(steps[0], { opacity: 0.32, duration: 0.2 }, 1.4)
    .to(steps[1], { opacity: 1, duration: 0.2 }, 1.4)
    .to($('i', steps[1]), { scaleY: 1, duration: 3 }, 1.4)
    .fromTo(scan, { opacity: 1, y: 0 }, { y: () => doc.offsetHeight - scan.offsetHeight, duration: 2.8 }, 1.5)
  const status = { n: 0 }
  tl.to(status, {
    n: 8, duration: 2.7, onUpdate: () => {
      const k = Math.round(status.n)
      wfS.textContent = k >= 8 ? 'Póliza cargada en 6 segundos' : k === 0 ? 'Leyendo la póliza con IA' : `Leyendo con IA: ${k} de 8 datos`
    },
  }, 1.5)
  texts.forEach((x, k) => {
    const at = 1.65 + k * 0.32; const o = { n: 0 }
    tl.to(lines[k], { backgroundColor: 'rgba(56,189,248,.24)', duration: 0.12 }, at)
      .to(fields[k], { boxShadow: 'inset 0 0 0 1.5px rgba(56,189,248,1), 0 0 0 5px rgba(56,189,248,.16)', duration: 0.12 }, at)
      .to(o, {
        n: x.t.length, duration: 0.24, onUpdate: () => {
          const c = Math.round(o.n)
          if (x.plate && c >= x.t.length) x.v.innerHTML = `<span class="plate"><em>ARGENTINA</em><b>${x.t}</b></span>`
          else x.v.textContent = x.t.slice(0, c)
        },
      }, at + 0.06)
      .to($('.c', fields[k]), { opacity: 1, scale: 1, duration: 0.18, ease: 'back.out(3)' }, at + 0.28)
      .to(bar, { scaleX: (k + 1) / 9, duration: 0.2 }, at + 0.1)
      .to(lines[k], { backgroundColor: 'rgba(56,189,248,0)', duration: 0.2 }, at + 0.4)
      .to(fields[k], { boxShadow: 'inset 0 0 0 1px rgba(226,232,240,1), 0 0 0 0px rgba(56,189,248,0)', duration: 0.2 }, at + 0.4)
  })
  tl.to(scan, { opacity: 0, duration: 0.2 }, 4.3)
    .to(bar, { scaleX: 1, duration: 0.2 }, 4.3)
    .to(cuotas, { backgroundColor: '#38BDF8', duration: 0.12, stagger: 0.03 }, 4.35)
    .to(steps[1], { opacity: 0.32, duration: 0.2 }, 4.6)
    .to(steps[2], { opacity: 1, duration: 0.2 }, 4.6)
    .to($('i', steps[2]), { scaleY: 1, duration: 2.6 }, 4.6)
    .to(aip, { xPercent: 0, duration: 0.55, ease: 'power3.out' }, 4.7)
    .to(ai, { opacity: 1, y: 0, duration: 0.3, ease: 'power3.out' }, 5.2)
  const qo = { n: 0 }
  tl.to(qo, { n: qt.length, duration: 0.8, onUpdate: () => { q.textContent = qt.slice(0, Math.round(qo.n)) } }, 5.6)
    .to(rcs, { opacity: 1, y: 0, duration: 0.3, stagger: 0.18, ease: 'power3.out' }, 6.5)
    .to({}, { duration: 0.6 }, 7.2)
}

/** Copilot: questions type themselves, the AI thinks, answers stream in. */
function wireCopilot(): void {
  const sec = $('#copiloto')
  if (!sec) return
  const body = $('.cw-body', sec)!; const typed = $('.cw-typed', sec)!; const ph = $('.cw-ph', sec)!; const box = $('.copi-chips', sec)!
  // The server renders the first answer for no-JS readers. Start from the prototype's empty
  // window now, at boot: clearing it later (when a menu jump flies past the chapter) would
  // shrink the page above the landing target and miss it by the answer's height on mobile.
  body.innerHTML = ''
  QA.forEach((x, i) => {
    const b = document.createElement('button')
    b.type = 'button'; b.className = 'qchip'; b.textContent = x.q; b.setAttribute('aria-pressed', 'false')
    b.addEventListener('click', () => { void play(i, true) })
    box.appendChild(b)
  })
  const chips = $$('.qchip', box)
  let run = 0; let next = 0; let user = false; let inView = false; let timer = 0; let driven = false
  // A step goes on only while this run is current and the chapter is on screen (or the visitor
  // picked the question): a menu jump that flies past the chapter must not grow the page later.
  const wait = (ms: number, id: number) => new Promise<boolean>((r) => setTimeout(() => r(id === run && (inView || driven)), ms))
  const stop = () => {
    inView = false; clearTimeout(timer)
    if (driven) return
    run++
    typed.textContent = ''; ph.style.display = ''
  }
  async function play(i: number, byUser = false) {
    if (byUser) user = true
    driven = byUser
    const id = ++run
    clearTimeout(timer)
    chips.forEach((c, k) => c.setAttribute('aria-pressed', String(k === i)))
    const x = QA[i]
    body.innerHTML = ''; ph.style.display = 'none'; typed.textContent = ''
    for (let k = 1; k <= x.q.length; k++) { typed.textContent = x.q.slice(0, k); if (!(await wait(26, id))) return }
    if (!(await wait(280, id))) return
    typed.textContent = ''; ph.style.display = ''
    const me = document.createElement('div'); me.className = 'cb-me'; me.textContent = x.q; body.appendChild(me)
    G.from(me, { y: 16, opacity: 0, scale: 0.96, duration: 0.45, ease: 'back.out(1.7)' })
    const ai = document.createElement('div'); ai.className = 'cb-ai'
    ai.innerHTML = '<span class="orb think"></span><div><div class="think-t">Pensando en tu cartera…</div></div>'
    body.appendChild(ai)
    G.from(ai, { opacity: 0, y: 10, duration: 0.35 })
    if (!(await wait(1150, id))) return
    const colEl = ai.lastElementChild as HTMLElement
    colEl.innerHTML = '<div class="cb-txt"></div>'
    $('.orb', ai)!.classList.remove('think')
    const txt = $('.cb-txt', colEl)!; const words = x.a.split(' ')
    for (let k = 1; k <= words.length; k++) { txt.textContent = words.slice(0, k).join(' '); if (!(await wait(36, id))) return }
    if (x.cards.length) { colEl.insertAdjacentHTML('beforeend', cardsHTML(x)); G.from($$('.cc', colEl), { y: 18, opacity: 0, scale: 0.96, duration: 0.5, stagger: 0.1, ease: 'power3.out' }) }
    colEl.insertAdjacentHTML('beforeend', actsHTML(x))
    G.from($$('.cb-acts span', colEl), { y: 10, opacity: 0, duration: 0.4, stagger: 0.08, delay: 0.25 })
    if (!user && inView) timer = window.setTimeout(() => { next = (i + 1) % QA.length; void play(next) }, 4400)
  }
  ST.create({
    trigger: sec, start: 'top 62%', end: 'bottom 25%',
    onEnter: () => { inView = true; void play(next) },
    onEnterBack: () => { inView = true; if (!user) void play(next) },
    onLeave: stop,
    onLeaveBack: stop,
  })
  G.from('.copi-win', { y: 80, rotateX: 16, opacity: 0, transformPerspective: 1400, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: '.copi-ui', start: 'top 85%' } })
  ST.batch('.copi-feats li', { start: 'top 90%', once: true, onEnter: (b) => G.from(b, { y: 30, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.1 }) })
}

/** Multicotizador: companies fly in, quote, the best price wins, and the policy is issued. */
let winnerPicked = false

/**
 * Picks this visit's quoter winner, once. The hero feed quotes its price, so the entrypoint
 * calls this before the hero; the quoter chapter, wired later, reuses the same pick.
 */
export function pickQuoteWinner(): void {
  if (winnerPicked) return
  const sec = $('#cotizador')
  if (!sec) return
  const tiles = $$('.tile', sec)
  if (!tiles.length) return
  winnerPicked = true
  // A different winner each visit, so no partner is favoured.
  const min = Math.min(...tiles.map((t) => Number(t.dataset.p)))
  const pick = tiles[Math.floor(Math.random() * tiles.length)]
  const old = $('.tile[data-best]', sec)
  if (old && old !== pick) {
    old.removeAttribute('data-best')
    pick.setAttribute('data-best', '')
    pick.dataset.p = String(min - 400 - Math.round(Math.random() * 16) * 100)
    $('.pr', pick)!.textContent = `$ ${fmt(Number(pick.dataset.p))}`
  }
  document.documentElement.dataset.best = `$ ${fmt(Number(pick.dataset.p))}`
  $('.emit-co', sec)!.textContent = $('b', pick)!.textContent
}

function wireQuoter(): void {
  const sec = $('#cotizador')
  if (!sec) return
  const tiles = $$('.tile', sec)
  if (!tiles.length) return
  pickQuoteWinner()

  const best = $('.tile[data-best]', sec) || tiles[0]
  const qn = $('.qn', sec)!
  const emit = $('.emit', sec)!; const esteps = $$('.emit-steps li', sec); const eok = $('.emit-ok', sec)!
  const prices = tiles.map((t) => { const pr = $('.pr', t)!; const v = Number(t.dataset.p); pr.textContent = '$ 0'; return { pr, v } })
  qn.textContent = '0'
  G.set(emit, { xPercent: -50, yPercent: -50, opacity: 0, scale: 0.86, y: 40 })
  G.set(eok, { opacity: 0, y: 8 })
  const tl = G.timeline({ defaults: { ease: 'none' }, paused: true }); autoplay(tl, sec)
  tl.fromTo(tiles, { x: () => rand(-700, 700), y: () => rand(-420, 420), z: () => rand(-1400, 200), rotationX: () => rand(-90, 90), rotationY: () => rand(-90, 90), opacity: 0 },
    { x: 0, y: 0, z: 0, rotationX: 0, rotationY: 0, opacity: 1, duration: 1.6, ease: 'power3.out', stagger: { each: 0.035, from: 'random' } }, 0)
  const qc = { n: 0 }
  tl.to(qc, { n: 32, duration: 1.5, onUpdate: () => { qn.textContent = String(Math.round(qc.n)) } }, 1.1)
  prices.forEach((p, k) => {
    const o = { v: 0 }
    tl.to(o, { v: p.v, duration: 0.5, onUpdate: () => { p.pr.textContent = `$ ${fmt(o.v)}` } }, 1.2 + (k % 8) * 0.06 + Math.floor(k / 8) * 0.1)
  })
  tl.to(tiles.filter((t) => t !== best), { opacity: 0.32, duration: 0.4 }, 2.9)
    .fromTo(best, { scale: 1, z: 0, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08), 0 0 0 0 rgba(34,197,94,0)' },
      { scale: 1.12, z: 80, boxShadow: 'inset 0 0 0 2px rgba(34,197,94,1), 0 24px 60px -12px rgba(34,197,94,0.65)', duration: 0.5, ease: 'back.out(2)' }, 2.95)
    .fromTo($('.best', best), { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3 }, 3.1)
    .to(tiles, { opacity: 0.1, duration: 0.4 }, 3.8)
    .to(emit, { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.6)' }, 3.85)
  esteps.forEach((li, k) => {
    const at = 4.4 + k * 0.38
    tl.to($('i', li), { backgroundColor: '#22C55E', boxShadow: 'inset 0 0 0 0px rgba(203,213,225,1)', duration: 0.15 }, at)
      .to($('i svg', li), { opacity: 1, duration: 0.15 }, at)
      .to(li, { color: '#0F172A', duration: 0.15 }, at)
  })
  tl.to(eok, { opacity: 1, y: 0, duration: 0.3, ease: 'back.out(2)' }, 6)
    .to({}, { duration: 0.6 }, 6.3)
}

/** Cobranzas y comisiones: a self-playing tour in four stops, with tabs that jump and follow the story. */
function wirePlata(): void {
  const sec = $('#plata')
  if (!sec) return
  const track = $('.hz-track', sec)!; const panels = $$('.hz-p', sec); const navs = $$('.hz-nav i', sec)
  const xFor = (i: number) => () => -(panels[i].offsetLeft - (innerWidth - panels[i].offsetWidth) / 2)
  const cells = $$('.cu', sec); const pts = [$('.pt1', sec), $('.pt2', sec)]
  const fpaths = $$<SVGPathElement>('.fl', sec); const fdots = $$('.fdot', sec); const n12 = [$('.n1', sec), $('.n2', sec)]
  const tlines = $$<SVGPathElement>('.tl-l', sec); const tn = [$('.t1', sec), $('.t2', sec), $('.t3', sec)]; const ts = $('.tree-s', sec)
  const rows = $$('tbody tr', sec); const bad = rows.filter((r) => r.hasAttribute('data-bad'))
  const bn = $('.bn', sec)!; const lt = $('.lt', sec)!; const btns = $$('.liq-f .pbtn', sec)
  const dash = (p: SVGPathElement) => { const L = p.getTotalLength(); p.style.strokeDasharray = String(L); p.style.strokeDashoffset = String(L) }
  fpaths.forEach(dash); tlines.forEach(dash)
  G.set(navs, { '--p': 0 })
  G.set(pts, { opacity: 0, y: 10 })
  G.set(fdots, { opacity: 0 })
  G.set(n12, { opacity: 0, scale: 0.8 })
  G.set(tn, { opacity: 0, scale: 0.8 })
  G.set(ts, { opacity: 0, y: -8 })
  G.set(rows, { opacity: 0, y: 18 })
  G.set(bad.map((r) => $('.dif', r)), { opacity: 0 })
  G.set(bad.map((r) => $('.st', r)), { opacity: 0, scale: 0.6 })
  G.set(btns, { opacity: 0, y: 10 })
  bn.textContent = '$ 0'; lt.textContent = '$ 0'
  const tl = G.timeline({ defaults: { ease: 'none' }, paused: true }); autoplay(tl, sec)
  const counts = (els: HTMLElement[], at: number) => els.forEach((b) => {
    const v = Number(b.dataset.v); const o = { v: 0 }; b.textContent = '$ 0'
    tl.to(o, { v, duration: 0.45, onUpdate: () => { b.textContent = `$ ${fmt(o.v)}` } }, at)
  })
  const paid = (c: HTMLElement, at: number) => tl.to($('.e1', c), { opacity: 1, duration: 0.1 }, at).to($$('.e0, .e2, .e3', c), { opacity: 0, duration: 0.1 }, at).to(c, { boxShadow: 'inset 0 0 0 1px rgba(34,197,94,.5)', duration: 0.1 }, at)
  // 1. Planes de pago.
  tl.to(navs[0], { '--p': 1, duration: 1.4 }, 0)
  cells.slice(0, 5).forEach((c, k) => paid(c, 0.1 + k * 0.12))
  tl.to($('.e2', cells[5]), { opacity: 1, duration: 0.1 }, 0.8).to($('.e0', cells[5]), { opacity: 0, duration: 0.1 }, 0.8)
    .to(cells[5], { boxShadow: 'inset 0 0 0 1.5px rgba(239,68,68,.7)', duration: 0.1 }, 0.8)
    .to($('.e3', cells[6]), { opacity: 1, duration: 0.1 }, 0.9).to($('.e0', cells[6]), { opacity: 0, duration: 0.1 }, 0.9)
    .to(cells[6], { boxShadow: 'inset 0 0 0 1.5px rgba(245,158,11,.7)', duration: 0.1 }, 0.9)
  paid(cells[5], 1.15)
  tl.to(pts[0], { opacity: 1, y: 0, duration: 0.15 }, 1.15).to(pts[1], { opacity: 1, y: 0, duration: 0.15 }, 1.3)
  // 2. Cobranza y rendición.
  tl.to(track, { x: xFor(1), duration: 0.6, ease: 'power2.inOut' }, 1.55).to(navs[1], { '--p': 1, duration: 1.3 }, 2.15)
  counts($$('.n0 .fv', sec), 2.15)
  tl.to(fpaths, { strokeDashoffset: 0, duration: 0.5, stagger: 0.1 }, 2.25)
    .to(n12, { opacity: 1, scale: 1, duration: 0.25, stagger: 0.12, ease: 'back.out(2)' }, 2.6)
    .to(fdots, { opacity: 1, duration: 0.2 }, 2.65)
  counts($$('.n1 .fv, .n2 .fv', sec), 2.65)
  // 3. Esquemas comisionales.
  tl.to(track, { x: xFor(2), duration: 0.6, ease: 'power2.inOut' }, 3.5).to(navs[2], { '--p': 1, duration: 1.3 }, 4.1)
  counts($$('.t0 .fv', sec), 4.1)
  tl.to(tlines, { strokeDashoffset: 0, duration: 0.4, stagger: 0.12 }, 4.15)
    .to(ts, { opacity: 1, y: 0, duration: 0.2 }, 4.15)
    .to(tn, { opacity: 1, scale: 1, duration: 0.25, stagger: 0.15, ease: 'back.out(2)' }, 4.4)
  counts($$('.t1 .fv, .t2 .fv, .t3 .fv', sec), 4.5)
  // 4. Conciliación.
  tl.to(track, { x: xFor(3), duration: 0.6, ease: 'power2.inOut' }, 5.45).to(navs[3], { '--p': 1, duration: 1.6 }, 6.05)
  tl.to(rows, { opacity: 1, y: 0, duration: 0.3, stagger: 0.06, ease: 'power3.out' }, 6.05)
  bad.forEach((r, k) => {
    const at = 6.45 + k * 0.3
    tl.to($$('td', r), { backgroundColor: 'rgba(239,68,68,.08)', duration: 0.12 }, at)
      .to($('.dif', r), { opacity: 1, duration: 0.1 }, at)
      .to($('.st', r), { opacity: 1, scale: 1, duration: 0.15, ease: 'back.out(3)' }, at + 0.04)
  })
  const m = { v: 0 }
  tl.to(m, { v: 184320, duration: 1, ease: 'power1.inOut', onUpdate: () => { const s = `$ ${fmt(m.v)}`; bn.textContent = s; lt.textContent = s } }, 6.45)
    .to(btns, { opacity: 1, y: 0, duration: 0.2, stagger: 0.1, ease: 'back.out(2)' }, 7.45)
    .to({}, { duration: 0.6 }, 7.65)

  // Tabs: the story moves the selection while it plays on its own. A visitor who picks a stop
  // stays on it: that stop's own animation plays and the story holds there instead of running on.
  const STOPS = [0, 1.55, 3.5, 5.45]
  const tabs = $$('.hz-nav [role="tab"]', sec)
  const select = (i: number) => tabs.forEach((t, k) => {
    t.setAttribute('aria-selected', String(k === i))
    t.tabIndex = k === i ? 0 : -1
  })
  STOPS.forEach((t, i) => tl.call(() => select(i), undefined, t + 0.01))
  let picked: gsap.core.Tween | null = null
  const jump = (i: number) => {
    picked?.kill()
    holdChapter(sec, () => { picked?.kill(); picked = null })
    tl.pause().seek(STOPS[i] + (i ? 0.62 : 0))
    select(i)
    picked = tl.tweenTo(i + 1 < STOPS.length ? STOPS[i + 1] : tl.duration(), { ease: 'none' })
  }
  tabs.forEach((b, i) => {
    b.addEventListener('click', () => jump(i))
    // Arrow keys move between tabs (WAI-ARIA tabs pattern, automatic activation).
    b.addEventListener('keydown', (e) => {
      const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
      const k = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : d ? (i + d + tabs.length) % tabs.length : -1
      if (k < 0) return
      e.preventDefault()
      tabs[k].focus()
      jump(k)
    })
  })
  select(0)
}

/** Stacking cards, each with a network that grows with the portfolio. */
function wireStack(): void {
  const cards = $$('.scard')
  cards.forEach((c, i) => {
    if (i === cards.length - 1) return
    G.fromTo(c, { scale: 1, filter: 'brightness(1)' }, { scale: 0.9 + i * 0.03, filter: 'brightness(0.4)', ease: 'none', scrollTrigger: { trigger: cards[i + 1], start: 'top bottom', end: 'top 14%', scrub: true } })
  })
  const NS = 'http://www.w3.org/2000/svg'
  $$<SVGSVGElement>('.net').forEach((svg) => {
    const level = Number(svg.dataset.net)
    const nodes: { x: number; y: number; r: number; c: string }[] = []; const links: [number, number][] = []
    const add = (x: number, y: number, r: number, c: string) => { nodes.push({ x, y, r, c }); return nodes.length - 1 }
    const ring = (parent: number, n: number, R: number, r: number, c: string, a0 = 0) => {
      const p = nodes[parent]; const out: number[] = []
      for (let k = 0; k < n; k++) { const a = a0 + (k / n) * Math.PI * 2; const id = add(p.x + Math.cos(a) * R, p.y + Math.sin(a) * R, r, c); links.push([parent, id]); out.push(id) }
      return out
    }
    const root = add(200, 200, 13, '#FFFFFF')
    if (level === 1) { ring(root, 8, 92, 5.5, '#38BDF8', 0.3); ring(root, 12, 160, 3.4, '#7DD3FC', 0.1) }
    if (level === 2) { const team = ring(root, 5, 84, 8.5, '#A78BFA', -Math.PI / 2); team.forEach((t, k) => ring(t, 7, 46, 3.6, '#38BDF8', k)) }
    if (level === 3) {
      const org = ring(root, 4, 80, 9.5, '#A78BFA', Math.PI / 4)
      org.forEach((o, k) => { const prods = ring(o, 4, 54, 5.6, '#60A5FA', k * 0.8 + 0.4); prods.forEach((p, j) => ring(p, 5, 22, 2.5, '#38BDF8', j)) })
    }
    const gl1 = document.createElementNS(NS, 'g'); const gl2 = document.createElementNS(NS, 'g')
    const ls: SVGLineElement[] = []; const cs: SVGCircleElement[] = []
    links.forEach(([a, b]) => {
      const l = document.createElementNS(NS, 'line')
      l.setAttribute('x1', String(nodes[a].x)); l.setAttribute('y1', String(nodes[a].y)); l.setAttribute('x2', String(nodes[b].x)); l.setAttribute('y2', String(nodes[b].y))
      const L = Math.hypot(nodes[b].x - nodes[a].x, nodes[b].y - nodes[a].y)
      l.style.strokeDasharray = String(L); l.style.strokeDashoffset = String(L)
      gl1.appendChild(l); ls.push(l)
    })
    nodes.forEach((n) => { const c = document.createElementNS(NS, 'circle'); c.setAttribute('cx', String(n.x)); c.setAttribute('cy', String(n.y)); c.setAttribute('r', String(n.r)); c.style.fill = n.c; gl2.appendChild(c); cs.push(c) })
    svg.append(gl1, gl2)
    G.set(cs, { scale: 0, transformOrigin: '50% 50%' })
    ST.create({
      trigger: svg, start: 'top 80%', once: true, onEnter: () => {
        G.to(ls, { strokeDashoffset: 0, duration: 0.8, stagger: 0.012, ease: 'power2.out' })
        G.to(cs, { scale: 1, duration: 0.6, stagger: 0.012, ease: 'back.out(2.5)' })
        G.to(cs[0], { scale: 1.25, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 1 })
      },
    })
  })
}

/** Centro de envíos: the campaign leaves in a burst of envelopes (distance scaled by viewport). */
function wireMail(): void {
  const sec = $('#envios')
  if (!sec) return
  const stage = $('.mail-stage', sec)!; const burst = $('.burst', sec)!; const send = $('.mw-send', sec)!; const prog = $('.mw-prog i', sec)!
  const stats = $$('[data-m]', sec).map((b) => ({ b, v: Number(b.dataset.m) }))
  let busy = false
  G.fromTo($('.win', sec), { rotateX: 18, y: 70, opacity: 0, transformPerspective: 1400 }, { rotateX: 0, y: 0, opacity: 1, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: sec, start: 'top 80%' } })
  const play = () => {
    if (busy) return
    busy = true
    burst.innerHTML = ''
    G.set(prog, { scaleX: 0 })
    stats.forEach((s) => { s.b.textContent = '0' })
    const sr = stage.getBoundingClientRect(); const br = send.getBoundingClientRect()
    const ox = br.left - sr.left + br.width / 2; const oy = br.top - sr.top + br.height / 2
    const tl = G.timeline({ delay: 0.5, onComplete: () => { busy = false } })
    tl.to(send, { scale: 0.9, duration: 0.12, yoyo: true, repeat: 1, ease: 'power2.out' }, 0)
    for (let i = 0; i < 36; i++) {
      const e = document.createElement('i'); e.className = 'env-i'; burst.appendChild(e)
      const a = -Math.PI / 2 + (Math.random() - 0.35) * 2.2; const d = (200 + Math.random() * 420) * Math.min(1, innerWidth / 1000)
      G.set(e, { x: ox - 15, y: oy - 10, scale: 0.3, opacity: 0, rotation: (Math.random() - 0.5) * 40 })
      const at = 0.15 + i * 0.03
      tl.to(e, { opacity: 1, scale: 0.7 + Math.random() * 0.5, duration: 0.2 }, at)
        .to(e, { x: ox + Math.cos(a) * d + 140, y: oy + Math.sin(a) * d * 0.85, rotation: (Math.random() - 0.5) * 140, duration: 1.1 + Math.random() * 0.5, ease: 'power2.out' }, at)
        .to(e, { opacity: 0, duration: 0.4 }, at + 0.85 + Math.random() * 0.3)
    }
    tl.to(prog, { scaleX: 1, duration: 1.8, ease: 'power2.inOut' }, 0.15)
    stats.forEach((s, k) => { const o = { v: 0 }; tl.to(o, { v: s.v, duration: 1.6 + k * 0.3, ease: 'power3.out', onUpdate: () => { s.b.textContent = fmt(o.v) } }, 0.2 + k * 0.25) })
  }
  ST.create({ trigger: sec, start: 'top 50%', onEnter: play, onEnterBack: play })
}

/** WhatsApp: the conversation plays when the phone comes into view. */
function wireWhatsapp(): void {
  const sec = $('#whatsapp')
  if (!sec) return
  const [w1, w2, w3, w4, w5] = ['.w1', '.w2', '.w3', '.w4', '.w5'].map((s) => $(s, sec))
  const tl = G.timeline({ paused: true })
  tl.set([w1, w3, w4, w5], { opacity: 0, y: 14, scale: 0.96 })
    .set(w2, { opacity: 0, display: 'inline-flex' })
    .to(w1, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: 'back.out(1.7)' }, 0.3)
    .to(w2, { opacity: 1, duration: 0.25 }, 0.9)
    .set(w2, { display: 'none' }, 2.2)
    .to(w3, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'back.out(1.7)' }, 2.2)
    .to(w4, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: 'back.out(1.7)' }, 3.5)
    .to(w5, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: 'back.out(1.7)' }, 4.5)
  tl.progress(0)
  G.from('.phone', { y: 80, rotate: -6, opacity: 0, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: '.phone', start: 'top 85%' } })
  ST.create({ trigger: sec, start: 'top 55%', onEnter: () => tl.restart(), onEnterBack: () => tl.restart() })
}
